/**
 * Personality Profile Mapper Utility
 *
 * Converts personality breakdown data into structured profile classifications
 * for both job offers and applicant profiles.
 *
 * Shared logic used across the application for personality interpretation.
 */
import { TRAITS, BASELINE } from './personalityConstants.js'
import {
	getAxisMotivation,
	DIRECTIONAL_INSIGHTS_DATA
} from '~/components/Personality/utils/personalityLanguageWrapper'

export const COMPATIBLE_QUADRANTS = {
	initiator: ['red'],
	motivator: ['yellow'],
	inspirator: ['yellow'],
	advisor: ['green', 'yellow'],
	supporter: ['green'],
	coordinator: ['blue'],
	observer: ['blue'],
	reformer: ['red']
}

const isQuadrantCompatible = (trait, quadrant) =>
	COMPATIBLE_QUADRANTS[trait]?.includes(quadrant) || false

/**
 * Trait membership on the two cross-cutting axes (mirrors the backend
 * half-plane weighting in PersonalityClassification::mapProfile):
 *   - change-home traits sit on the Change side of the Change/Routine axis
 *   - people-core traits carry the people-domain relational copy
 *
 * Used by FR-2 branch composition to route section copy by trait domain.
 */
const CHANGE_HOME_TRAITS = ['initiator', 'motivator', 'inspirator', 'reformer']
const PEOPLE_CORE_TRAITS = ['advisor', 'inspirator', 'supporter', 'motivator']

/**
 * The single source of truth for which dossier "domain" each insight section
 * belongs to. Branch composition resolves a source per domain, then picks
 * every section from its domain's source — there is no second, hardcoded
 * routing table.
 *
 *   keyStrengths   — always the lead trait (B/C); the quadrant (A).
 *   relational     — people-domain copy.
 *   operatingMode  — facts/task-domain copy.
 *   workEnvironment — axis-driven (B) / facts-domain (C).
 */
const SECTION_DOMAIN = {
	keyStrengths: 'keyStrengths',
	workStyle: 'operatingMode',
	decisionMaking: 'operatingMode',
	adaptabilityToChange: 'operatingMode',
	communicationStyle: 'relational',
	collaborationStyle: 'relational',
	conflictResolution: 'relational',
	workEnvironment: 'workEnvironment'
}

/**
 * Build the full set of insight translation keys for a single trait.
 * Mirrors the TRAIT branch of getProfileDisplay so Branch B/C composition
 * can pull any section from a lead or co-lead trait.
 */
const buildTraitInsights = trait => ({
	workplaceSignal: `personality_results.trait.${trait}.workplace_signal`,
	signalDescription: `personality_results.trait.${trait}.signal_description`,
	description: `personality_results.trait.${trait}.description`,
	keyStrengths: [
		`personality_results.trait.${trait}.key_strengths.1`,
		`personality_results.trait.${trait}.key_strengths.2`,
		`personality_results.trait.${trait}.key_strengths.3`
	],
	workStyle: [
		`personality_results.trait.${trait}.work_style.1`,
		`personality_results.trait.${trait}.work_style.2`,
		`personality_results.trait.${trait}.work_style.3`
	],
	communicationStyle: `personality_results.trait.${trait}.communication_style`,
	decisionMaking: `personality_results.trait.${trait}.decision_making`,
	conflictResolution: `personality_results.trait.${trait}.conflict_resolution`,
	workEnvironment: `personality_results.trait.${trait}.work_environment`,
	collaborationStyle: `personality_results.trait.${trait}.collaboration_style`,
	adaptabilityToChange: `personality_results.trait.${trait}.adaptability_to_change`
})

/**
 * Resolve the co-lead authoring the people-domain copy in Branch C.
 *
 * Branch C is a co-lead tie; relational copy should come from whichever
 * co-lead is people-core, with the facts co-lead owning operating-mode.
 * When the co-leads share a domain (e.g. observer + reformer, both facts —
 * see golden `axis_routine_facts`), there is no people co-lead and the lead
 * trait authors the relational sections too. This keeps routing deterministic
 * and prevents the co-lead from arbitrarily owning a domain it doesn't
 * represent.
 *
 * @param {string|null} leadTrait
 * @param {string|null} coLeadTrait
 * @param {Object} leadInsights
 * @param {Object} coLeadInsights
 * @returns {{peopleInsights: Object, factsInsights: Object}} people/facts
 *   sources. When neither co-lead is people-core, peopleInsights falls back
 *   to the lead trait (which then owns both relational and, as lead,
 *   keyStrengths); factsInsights stays the facts co-lead.
 */
const resolveCoLeadDomains = (
	leadTrait,
	coLeadTrait,
	leadInsights,
	coLeadInsights
) => {
	const leadIsPeople =
		leadTrait && PEOPLE_CORE_TRAITS.includes(leadTrait)
	const coLeadIsPeople =
		coLeadTrait && PEOPLE_CORE_TRAITS.includes(coLeadTrait)

	// Exactly one co-lead is people-core → it owns relational; the other owns
	// operating-mode + workEnvironment.
	if (leadIsPeople && !coLeadIsPeople) {
		return { peopleInsights: leadInsights, factsInsights: coLeadInsights }
	}
	if (coLeadIsPeople && !leadIsPeople) {
		return { peopleInsights: coLeadInsights, factsInsights: leadInsights }
	}

	// Same domain (both people, or both facts): the lead trait authors the
	// relational sections (it is the dominant voice and should not cede a
	// domain to a co-lead that does not represent it); the co-lead still
	// authors operating-mode so neither trait is erased. For an
	// observer+reformer tie this means relational stays with observer (lead)
	// and operating-mode with reformer (co-lead).
	return {
		peopleInsights: leadInsights,
		factsInsights: coLeadInsights
	}
}

/**
 * Main personality profile mapping function.
 *
 * @param {Object} personalityBreakdown - Personality breakdown data from database
 * @param {number} personalityBreakdown.inspirator - Inspirator percentage
 * @param {number} personalityBreakdown.supporter - Supporter percentage
 * @param {number} personalityBreakdown.motivator - Motivator percentage
 * @param {number} personalityBreakdown.reformer - Reformer percentage
 * @param {number} personalityBreakdown.initiator - Initiator percentage
 * @param {number} personalityBreakdown.advisor - Advisor percentage
 * @param {number} personalityBreakdown.observer - Observer percentage
 * @param {number} personalityBreakdown.coordinator - Coordinator percentage
 * @returns {Object|null} ProfileResult object or null if insufficient data
 */
export const mapPersonalityProfile = personalityBreakdown => {
	if (!personalityBreakdown || typeof personalityBreakdown !== 'object') {
		return null
	}

	const hasPositiveTrait = Object.entries(personalityBreakdown).some(
		([trait, percent]) =>
			TRAITS.includes(trait) &&
			typeof percent === 'number' &&
			percent > 0
	)

	if (!hasPositiveTrait) {
		return null
	}

	// Fill missing traits with 0 (use raw percent scores)
	const filled = {}
	TRAITS.forEach(trait => {
		filled[trait] = personalityBreakdown[trait] || 0
	})

	// Normalize against baseline (12.5% = 1.0×)
	// NOTE: When using backend V2 data (APR-921), normalization is provided
	// in match.personality_v2.normalized_* and this calculation is skipped
	const normalized = {}
	TRAITS.forEach(trait => {
		normalized[trait] = filled[trait] / BASELINE
	})

	// Sort traits by normalized score (descending)
	const sortedNormalized = Object.entries(normalized).sort(
		(a, b) => b[1] - a[1]
	)

	const first = {
		trait: sortedNormalized[0][0],
		score: sortedNormalized[0][1],
		rawScore: filled[sortedNormalized[0][0]]
	}
	const second = {
		trait: sortedNormalized[1][0],
		score: sortedNormalized[1][1],
		rawScore: filled[sortedNormalized[1][0]]
	}
	const gap = first.score - second.score

	// TRAIT: Single dominant (normalized score >3.2 AND gap ≥1.2)
	if (first.score > 3.2 && gap >= 1.2) {
		return {
			approach: 'TRAIT',
			confidence: first.score > 3.6 && gap >= 2.0 ? 'high' : 'medium',
			primary: first.trait,
			primaryScore: first.rawScore,
			normalizedPrimary: first.score,
			secondary: second.trait,
			secondaryScore: second.rawScore,
			gap: gap,
			lead_in_quadrant: null,
			colead_cross_quadrant: false,
			branch: 'TRAIT',
			double_outvote: false
		}
	}

	// AXIS: Two-step quadrant classification
	// Calculate axis scores using half-plane weighted method.
	// Each trait contributes 2.0 total across all axes:
	// core traits (×1.0) + boundary traits shared with adjacent axis (×0.5).
	const change =
		filled.motivator * 1.0 +
		filled.initiator * 1.0 +
		filled.inspirator * 1.0 +
		filled.reformer * 0.5 +
		filled.advisor * 0.5
	const routine =
		filled.coordinator * 1.0 +
		filled.supporter * 1.0 +
		filled.observer * 1.0 +
		filled.advisor * 0.5 +
		filled.reformer * 0.5
	const people =
		filled.advisor * 1.0 +
		filled.inspirator * 1.0 +
		filled.supporter * 1.0 +
		filled.motivator * 0.5 +
		filled.coordinator * 0.5
	const facts =
		filled.reformer * 1.0 +
		filled.initiator * 1.0 +
		filled.observer * 1.0 +
		filled.motivator * 0.5 +
		filled.coordinator * 0.5

	// Step 2a: Quadrant routing — dominant side of each axis pair
	const routineChangeDominant = routine >= change ? 'routine' : 'change'
	const peopleFactsDominant = people >= facts ? 'people' : 'facts'

	// Step 2b: Primary/secondary — compare the two quadrant axis scores
	// head-to-head; the higher score is the primary (motivation driver)
	const routineChangeScore = routineChangeDominant === 'routine' ? routine : change
	const peopleFactsScore = peopleFactsDominant === 'people' ? people : facts

	let primaryAxis, secondaryAxis

	if (routineChangeScore >= peopleFactsScore) {
		primaryAxis = routineChangeDominant
		secondaryAxis = peopleFactsDominant
	} else {
		primaryAxis = peopleFactsDominant
		secondaryAxis = routineChangeDominant
	}

	// Map axis combination to quadrant color
	const quadrantMap = {
		'routine-people': 'green',
		'people-routine': 'green',
		'routine-facts': 'blue',
		'facts-routine': 'blue',
		'change-people': 'yellow',
		'people-change': 'yellow',
		'change-facts': 'red',
		'facts-change': 'red'
	}

	const quadrantKey = `${primaryAxis}-${secondaryAxis}`
	const quadrant = quadrantMap[quadrantKey]
	const leadInQuadrant = isQuadrantCompatible(first.trait, quadrant)
	const secondInQuadrant = isQuadrantCompatible(second.trait, quadrant)
	const coleadCrossQuadrant =
		first.rawScore - second.rawScore < 12 && !secondInQuadrant
	const branch = coleadCrossQuadrant ? 'C' : leadInQuadrant ? 'A' : 'B'
	const doubleOutvote = branch === 'B' && !secondInQuadrant

	return {
		approach: 'AXIS',
		quadrant,
		primaryAxis,
		secondaryAxis,
		axisDirectionKey: `${primaryAxis}_${secondaryAxis}`,
		axisScores: { routine, change, people, facts },
		topTraits: {
			first: { trait: first.trait, score: first.rawScore },
			second: { trait: second.trait, score: second.rawScore }
		},
		lead_in_quadrant: leadInQuadrant,
		colead_cross_quadrant: coleadCrossQuadrant,
		branch,
		double_outvote: doubleOutvote
	}
}

/**
 * Get workplace signal for a trait based on translation key structure
 *
 * @param {string} trait - Trait name (e.g., 'supporter', 'reformer')
 * @returns {string} Workplace signal for the trait
 */
export const getTraitWorkplaceSignal = trait => {
	return `personality_results.trait.${trait}.workplace_signal`
}

/**
 * Get signal description for a trait based on translation key structure
 *
 * @param {string} trait - Trait name (e.g., 'supporter', 'reformer')
 * @returns {string} Signal description for the trait
 */
export const getTraitSignalDescription = trait => {
	return `personality_results.trait.${trait}.signal_description`
}

/**
 * Get trait display name based on translation key structure
 *
 * @param {string} trait - Trait name (e.g., 'supporter', 'reformer')
 * @returns {string} Display name for the trait
 */
export const getTraitName = trait => {
	return `personality_results.trait.${trait}.name`
}

/**
 * Get quadrant label based on translation key structure
 *
 * @param {string} quadrant - Quadrant color ('red', 'yellow', 'green', 'blue')
 * @returns {string} Quadrant label
 */
export const getQuadrantLabel = quadrant => {
	return `personality_results.quadrant.${quadrant}.label`
}

/**
 * Get axis profile title based on translation key structure
 *
 * @param {string} axis - Axis type ('routine', 'change', 'people', 'facts')
 * @returns {string} Axis profile title
 */
export const getAxisProfileTitle = axis => {
	return `personality_results.axis_profile.${axis}.title`
}

/**
 * Get axis workplace signal based on translation key structure
 *
 * @param {string} axis - Axis color ('green', 'blue', 'yellow', 'red')
 * @returns {string} Axis workplace signal
 */
export const getAxisWorkplaceSignal = axis => {
	return `personality_results.axis_insights.${axis}.workplace_signal`
}

/**
 * Get axis signal description based on translation key structure
 *
 * @param {string} axis - Axis color ('green', 'blue', 'yellow', 'red')
 * @returns {string} Axis signal description
 */
export const getAxisSignalDescription = axis => {
	return `personality_results.axis_insights.${axis}.signal_description`
}

/**
 * Utility to validate personality breakdown data
 *
 * @param {Object} personalityBreakdown - Personality breakdown data
 * @returns {boolean} True if data is valid for mapping
 */
export const isValidPersonalityBreakdown = personalityBreakdown => {
	if (!personalityBreakdown || typeof personalityBreakdown !== 'object') {
		return false
	}

	// Check if at least one trait has a positive percentage value.
	// Pure profiles may only include their single non-zero trait.
	return Object.entries(personalityBreakdown).some(
		([trait, percent]) =>
			TRAITS.includes(trait) &&
			typeof percent === 'number' &&
			percent >= 0 &&
			percent <= 100 &&
			percent > 0
	)
}

/**
 * Get top N traits from personality breakdown
 *
 * @param {Object} personalityBreakdown - Personality breakdown data
 * @param {number} count - Number of top traits to return (default: 3)
 * @returns {Array} Array of {trait, percent} objects
 */
export const getTopTraits = (personalityBreakdown, count = 3) => {
	if (!isValidPersonalityBreakdown(personalityBreakdown)) {
		return []
	}

	return Object.entries(personalityBreakdown)
		.filter(
			([trait, percent]) =>
				TRAITS.includes(trait) &&
				typeof percent === 'number' &&
				percent > 0
		)
		.sort((a, b) => b[1] - a[1])
		.slice(0, count)
		.map(([trait, percent]) => ({ trait, percent }))
}

/**
 * Transforms the profile mapping result into display-ready data, composing
 * the dossier by branch (FR-2):
 *
 *   TRAIT   — all copy from the dominant trait.
 *   A       — lead trait sits inside the rendered quadrant → all quadrant copy.
 *   B       — lead trait is contradicted by the quadrant (gap ≥ 12):
 *             header + relational sections = quadrant; keyStrengths +
 *             operating-mode sections = lead trait; workEnvironment follows
 *             the Change/Routine axis agreement.
 *   C       — co-lead tie (gap < 12): header + keyStrengths from the lead
 *             trait; relational sections from the people co-lead; operating-
 *             mode + workEnvironment from the facts co-lead.
 *
 * @param {Object} personalityBreakdown - Personality breakdown data
 * @param {string} viewType - 'applicant' or 'hiring_manager' for context-specific content
 * @param {Object|null} [profileOverride] - Pre-computed classification
 *   (e.g. backend `personality_classification`). When present it is preferred
 *   over local recomputation so live API payloads route identically to fresh
 *   data; legacy payloads without it fall back to mapPersonalityProfile.
 * @returns {Object} ProfileDisplayResult with display text, description,
 *   profile type, and insights.
 */
export const getProfileDisplay = (
	personalityBreakdown,
	viewType = 'applicant',
	profileOverride = null
) => {
	let profile = profileOverride || mapPersonalityProfile(personalityBreakdown)

	if (!profile) {
		return {
			workplaceSignal: '',
			description: '',
			profileType: ''
		}
	}

	// A backend AXIS override marked Branch B/C must carry the lead/co-lead
	// traits the composer reads; a partial payload would emit
	// `personality_results.trait.undefined.*` keys and render blank dossier
	// sections. Recompute locally rather than trust a partial override.
	if (
		profile.approach === 'AXIS' &&
		(profile.branch === 'B' || profile.branch === 'C') &&
		!profile.topTraits?.first?.trait
	) {
		profile = mapPersonalityProfile(personalityBreakdown) || profile
	}

	if (profile.approach === 'TRAIT' && profile.primary) {
		const trait = profile.primary
		return {
			workplaceSignal: getTraitWorkplaceSignal(trait),
			signalDescription: getTraitSignalDescription(trait),
			description: `personality_results.trait.${trait}.description`,
			profileType: `personality_results.view.${viewType}.profile_type_prefix`,
			branch: 'TRAIT',
			leadTrait: trait,
			coLeadTrait: null,
			insights: {
				keyStrengths: [
					`personality_results.trait.${trait}.key_strengths.1`,
					`personality_results.trait.${trait}.key_strengths.2`,
					`personality_results.trait.${trait}.key_strengths.3`
				],
				workStyle: [
					`personality_results.trait.${trait}.work_style.1`,
					`personality_results.trait.${trait}.work_style.2`,
					`personality_results.trait.${trait}.work_style.3`
				],
				communicationStyle: `personality_results.trait.${trait}.communication_style`,
				decisionMaking: `personality_results.trait.${trait}.decision_making`,
				conflictResolution: `personality_results.trait.${trait}.conflict_resolution`,
				workEnvironment: `personality_results.trait.${trait}.work_environment`,
				collaborationStyle: `personality_results.trait.${trait}.collaboration_style`,
				adaptabilityToChange: `personality_results.trait.${trait}.adaptability_to_change`
			}
		}
	}

	// AXIS approach — compose by branch.
	const quadrant = profile.quadrant || 'green'
	const directionalKey = profile.axisDirectionKey
	const branch = profile.branch || 'A'
	const leadTrait = profile.topTraits?.first?.trait
	const coLeadTrait = profile.topTraits?.second?.trait

	// Quadrant/directional insight keys (existing path).
	const directionalData =
		DIRECTIONAL_INSIGHTS_DATA.getDirectionalData(directionalKey)
	const fallbackNs = `personality_results.axis_insights.${quadrant}`
	const quadrantInsights = directionalData || {
		workplaceSignal: `${fallbackNs}.workplace_signal`,
		signalDescription: `${fallbackNs}.signal_description`,
		description: `${fallbackNs}.description`,
		keyStrengths: [
			`${fallbackNs}.key_strengths.1`,
			`${fallbackNs}.key_strengths.2`,
			`${fallbackNs}.key_strengths.3`
		],
		workStyle: [
			`${fallbackNs}.work_style.1`,
			`${fallbackNs}.work_style.2`,
			`${fallbackNs}.work_style.3`
		],
		communicationStyle: `${fallbackNs}.communication_style`,
		decisionMaking: `${fallbackNs}.decision_making`,
		conflictResolution: `${fallbackNs}.conflict_resolution`,
		workEnvironment: `${fallbackNs}.work_environment`,
		collaborationStyle: `${fallbackNs}.collaboration_style`,
		adaptabilityToChange: `${fallbackNs}.adaptability_to_change`
	}

	const axisMotivationFragment = getAxisMotivation(directionalKey)
	const isMotivationTranslationMissing =
		axisMotivationFragment &&
		typeof axisMotivationFragment === 'string' &&
		axisMotivationFragment.startsWith('personality_results.axis_direction.')

	const motivationSentence =
		axisMotivationFragment &&
		typeof axisMotivationFragment === 'string' &&
		axisMotivationFragment.trim() &&
		!isMotivationTranslationMissing
			? `${axisMotivationFragment
					.trim()
					.replace(/^./, c => c.toUpperCase())
					.replace(/\.+$/, '')}.`
			: null

	const descriptionWithMotivation = motivationSentence
		? `${motivationSentence} ${quadrantInsights.description}`
		: quadrantInsights.description

	// Branch A: pure quadrant dossier (unchanged behaviour, made explicit).
	if (branch !== 'B' && branch !== 'C') {
		return {
			workplaceSignal: quadrantInsights.workplaceSignal,
			signalDescription: quadrantInsights.signalDescription,
			description: descriptionWithMotivation,
			quadrant, // retained for UI color rendering and quadrant label
			profileType:
				`personality_results.view.${viewType}.profile_type_prefix` +
				' ' +
				getQuadrantLabel(quadrant),
			branch: 'A',
			leadTrait: leadTrait || null,
			coLeadTrait: coLeadTrait || null,
			insights: composeInsights('A', quadrantInsights, null, {
				leadTrait,
				coLeadTrait,
				quadrant
			})
		}
	}

	if (branch === 'B') {
		const leadInsights = leadTrait ? buildTraitInsights(leadTrait) : {}
		// workEnvironment follows the Change/Routine axis: lead-trait when the
		// lead and quadrant disagree on that axis, otherwise the quadrant.
		const leadIsChange = leadTrait
			? CHANGE_HOME_TRAITS.includes(leadTrait)
			: false
		const quadrantIsChange = quadrant === 'red' || quadrant === 'yellow'
		const workEnvDomain =
			leadIsChange === quadrantIsChange ? 'quadrant' : 'lead-trait'

		return {
			workplaceSignal: quadrantInsights.workplaceSignal,
			signalDescription: quadrantInsights.signalDescription,
			description: descriptionWithMotivation,
			quadrant,
			profileType:
				`personality_results.view.${viewType}.profile_type_prefix` +
				' ' +
				getQuadrantLabel(quadrant),
			branch: 'B',
			leadTrait: leadTrait || null,
			coLeadTrait: coLeadTrait || null,
			insights: composeInsights('B', quadrantInsights, leadInsights, {
				workEnvDomain
			})
		}
	}

	// Branch C: co-lead tie — compose from both co-leads by domain.
	const leadInsights = leadTrait ? buildTraitInsights(leadTrait) : {}
	const coLeadInsights = coLeadTrait ? buildTraitInsights(coLeadTrait) : {}

	// Route co-leads to people/facts domains. When the co-leads share a
	// domain (e.g. observer + reformer, both facts), the lead trait keeps
	// relational ownership — see resolveCoLeadDomains.
	const { peopleInsights, factsInsights } = resolveCoLeadDomains(
		leadTrait,
		coLeadTrait,
		leadInsights,
		coLeadInsights
	)

	return {
		workplaceSignal: leadInsights.workplaceSignal,
		signalDescription: leadInsights.signalDescription,
		description: leadInsights.description,
		quadrant,
		profileType:
			`personality_results.view.${viewType}.profile_type_prefix` +
			' ' +
			getQuadrantLabel(quadrant),
		branch: 'C',
		leadTrait: leadTrait || null,
		coLeadTrait: coLeadTrait || null,
		insights: composeInsights('C', quadrantInsights, leadInsights, {
			peopleInsights,
			factsInsights
		})
	}
}

/**
 * Assemble the `insights` object. SECTION_DOMAIN is the single routing map:
 * each section reads from whichever source object owns its domain, and the
 * per-branch `domainSource` resolver is the only place domain→source is set.
 *
 * Domain sources by branch:
 *   Branch A — every domain from the quadrant.
 *   Branch B — keyStrengths + operatingMode from the lead trait; relational
 *              from the quadrant; workEnvironment from the lead trait when
 *              the lead and quadrant disagree on the Change/Routine axis,
 *              otherwise the quadrant.
 *   Branch C — keyStrengths from the lead trait; relational from the people
 *              co-lead (lead trait when the co-leads share a domain);
 *              operatingMode + workEnvironment from the facts co-lead.
 *
 * @param {string} branch - 'A' | 'B' | 'C'
 * @param {Object} quadrantInsights - quadrant/directional copy
 * @param {Object} leadInsights - lead-trait copy (B operating-mode + C keyStrengths source)
 * @param {Object} ctx - { peopleInsights, factsInsights, workEnvDomain }
 */
const composeInsights = (branch, quadrantInsights, leadInsights, ctx) => {
	const domainSource = {
		keyStrengths:
			branch === 'A' ? quadrantInsights : leadInsights,
		operatingMode:
			branch === 'A'
				? quadrantInsights
				: branch === 'B'
				? leadInsights
				: ctx.factsInsights,
		relational:
			branch === 'A' || branch === 'B' ? quadrantInsights : ctx.peopleInsights,
		workEnvironment:
			branch === 'A'
				? quadrantInsights
				: branch === 'B'
				? ctx.workEnvDomain === 'lead-trait'
					? leadInsights
					: quadrantInsights
				: ctx.factsInsights
	}

	// SECTION_DOMAIN drives every section; no second routing table.
	return Object.fromEntries(
		Object.entries(SECTION_DOMAIN).map(([section, domain]) => [
			section,
			domainSource[domain][section]
		])
	)
}

/**
 * Enhanced profile display function using language wrapper
 * Provides better separation of concerns and context management
 *
 * @param {Object} personalityBreakdown - Personality breakdown data
 * @param {string} viewType - 'applicant' or 'hiring_manager' for context-specific content
 * @param {Object|null} [profileOverride] - Pre-computed classification
 *   (e.g. backend `personality_classification`); forwarded to getProfileDisplay.
 * @returns {Object} Enhanced ProfileDisplayResult with language wrapper integration
 */
export const getProfileDisplayWithWrapper = (
	personalityBreakdown,
	viewType = 'applicant',
	profileOverride = null
) => {
	const profile = profileOverride || mapPersonalityProfile(personalityBreakdown)

	if (!profile) {
		return {
			workplaceSignal: '',
			signalDescription: '',
			description: '',
			profileType: '',
			languageWrapper: {
				tabLabel: `personality_results.view.${viewType}.tab_label`,
				cardTitle: `personality_results.view.${viewType}.card_title`,
				cardDescription: `personality_results.view.${viewType}.card_description`,
				sectionTitles: {
					keyStrengths: `personality_results.view.${viewType}.section.key_strengths`,
					workStyle: `personality_results.view.${viewType}.section.work_style`,
					communicationStyle: `personality_results.view.${viewType}.section.communication_style`,
					decisionMaking: `personality_results.view.${viewType}.section.decision_making`,
					conflictResolution: `personality_results.view.${viewType}.section.conflict_resolution`,
					workEnvironment: `personality_results.view.${viewType}.section.work_environment`,
					collaborationStyle: `personality_results.view.${viewType}.section.collaboration_style`,
					adaptabilityToChange: `personality_results.view.${viewType}.section.adaptability_to_change`
				}
			}
		}
	}

	const baseDisplay = getProfileDisplay(
		personalityBreakdown,
		viewType,
		profileOverride
	)

	return {
		...baseDisplay,
		languageWrapper: {
			tabLabel: `personality_results.view.${viewType}.tab_label`,
			cardTitle: `personality_results.view.${viewType}.card_title`,
			cardDescription: `personality_results.view.${viewType}.card_description`,
			profileTypePrefix: `personality_results.view.${viewType}.profile_type_prefix`,
			sectionTitles: {
				keyStrengths: `personality_results.view.${viewType}.section.key_strengths`,
				workStyle: `personality_results.view.${viewType}.section.work_style`,
				communicationStyle: `personality_results.view.${viewType}.section.communication_style`,
				decisionMaking: `personality_results.view.${viewType}.section.decision_making`,
				conflictResolution: `personality_results.view.${viewType}.section.conflict_resolution`,
				workEnvironment: `personality_results.view.${viewType}.section.work_environment`,
				collaborationStyle: `personality_results.view.${viewType}.section.collaboration_style`,
				adaptabilityToChange: `personality_results.view.${viewType}.section.adaptability_to_change`
			}
		}
	}
}

// Default export for convenience
export default {
	mapPersonalityProfile,
	getTraitWorkplaceSignal,
	getTraitName,
	getQuadrantLabel,
	getAxisProfileTitle,
	getAxisWorkplaceSignal,
	isValidPersonalityBreakdown,
	getTopTraits,
	getProfileDisplay,
	getProfileDisplayWithWrapper,
	TRAITS,
	BASELINE,
	COMPATIBLE_QUADRANTS
}
